<script setup lang="ts">
import { ref, watch, computed } from "vue";
import type { StarShape } from "@/stores/ratingService";
import { RatingServiceType } from "@/stores/ratingService";

export interface ServiceData {
  service_key: string;
  name: string;
  type: RatingServiceType;
  min_stars?: 0 | 1;
  max_stars?: number;
  star_shape?: StarShape;
}

const props = withDefaults(
  defineProps<{
    open: boolean;
    isEditing?: boolean;
    initialData?: ServiceData | null;
    showWarning?: boolean;
  }>(),
  {
    isEditing: false,
    initialData: null,
    showWarning: true,
  }
);

const emit = defineEmits<{
  (e: "save", data: ServiceData): void;
  (e: "close"): void;
}>();

const serviceKey = ref("");
const serviceName = ref("");
const serviceType = ref<RatingServiceType>(RatingServiceType.Stars);
const minStars = ref<0 | 1>(0);
const maxStars = ref<number>(5);
const starShape = ref<StarShape>("fat star");

const starShapeOptions: StarShape[] = [
  "circle",
  "square",
  "fat star",
  "pentagram star",
  "six point star",
  "eight point star",
  "x shape",
  "diamond",
  "heart",
];

const isStarsType = computed(() => serviceType.value === RatingServiceType.Stars);
const isGraphicalType = computed(
  () => serviceType.value === RatingServiceType.Stars || serviceType.value === RatingServiceType.Like
);

watch(
  () => props.initialData,
  (newData) => {
    if (newData) {
      serviceKey.value = newData.service_key;
      serviceName.value = newData.name;
      serviceType.value = newData.type;
      minStars.value = newData.min_stars ?? 0;
      maxStars.value = newData.max_stars ?? 5;
      starShape.value = newData.star_shape ?? "fat star";
    }
  },
  { immediate: true }
);

function reset() {
  serviceKey.value = "";
  serviceName.value = "";
  serviceType.value = RatingServiceType.Stars;
  minStars.value = 0;
  maxStars.value = 5;
  starShape.value = "fat star";
}

function onSave() {
  if (!serviceKey.value.trim() || !serviceName.value.trim()) {
    alert("Service key and name are required");
    return;
  }

  const data: ServiceData = {
    service_key: serviceKey.value,
    name: serviceName.value,
    type: serviceType.value,
  };

  if (isStarsType.value) {
    data.min_stars = minStars.value;
    data.max_stars = maxStars.value;
    data.star_shape = starShape.value;
  } else if (isGraphicalType.value) {
    data.star_shape = starShape.value;
  }

  emit("save", data);
  reset();
}

function onClose() {
  emit("close");
  reset();
}
</script>

<template>
  <div v-if="open" class="service-form-modal-backdrop" @click.self="onClose">
    <div class="service-form-modal">
      <div class="modal-header">
        <h4 class="modal-title">{{ isEditing ? "Edit Service" : "Add Service" }}</h4>
        <button class="modal-close" @click="onClose" aria-label="Close">✕</button>
      </div>

      <div class="modal-body">
        <div v-if="showWarning && !isEditing" class="form-warning">
          ⚠️ <strong>Advanced only.</strong> You must create the service in Hydrus first. Usually, you should just click
          "Refresh from API" and select it from the list.
        </div>

        <label class="form-label">
          Service Key:
          <input v-model="serviceKey" type="text" placeholder="e.g., my-custom-service" />
        </label>

        <label class="form-label">
          Name:
          <input v-model="serviceName" type="text" placeholder="Display name" />
        </label>

        <label class="form-label">
          Type:
          <select v-model.number="serviceType">
            <option :value="RatingServiceType.Stars">Stars</option>
            <option :value="RatingServiceType.Like">Like/Dislike</option>
            <option :value="RatingServiceType.Counter">Counter</option>
          </select>
        </label>

        <div v-if="isStarsType" class="conditional-fields">
          <label class="form-label">
            Min Stars:
            <select v-model.number="minStars">
              <option :value="0">0</option>
              <option :value="1">1</option>
            </select>
          </label>
          <label class="form-label">
            Max Stars:
            <select v-model.number="maxStars">
              <option v-for="n in 20" :key="n" :value="n">{{ n }}</option>
            </select>
          </label>
        </div>

        <div v-if="isGraphicalType" class="conditional-fields">
          <label class="form-label">
            Star Shape:
            <select v-model="starShape">
              <option v-for="shape in starShapeOptions" :key="shape" :value="shape">
                {{ shape }}
              </option>
            </select>
          </label>
        </div>

        <div class="form-actions">
          <button class="btn-save" @click="onSave">{{ isEditing ? "Save Changes" : "Add Service" }}</button>
          <button class="btn-cancel" @click="onClose">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.service-form-modal-backdrop {
  position: fixed;
  inset: 0;
  background: color-mix(in srgb, var(--bg-surface) 50%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
}

.service-form-modal {
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  width: min(400px, 95vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);

  .modal-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  .modal-close {
    background: none;
    border: none;
    color: var(--text-primary);
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;

    &:hover {
      color: var(--accent);
    }
  }
}

.modal-body {
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-warning {
  padding: 8px 10px;
  background: color-mix(in srgb, var(--danger, #e55) 10%, var(--bg-surface));
  border: 1px solid color-mix(in srgb, var(--danger, #e55) 30%, var(--border));
  border-radius: var(--radius-sm);
  font-size: 0.8em;
  color: var(--text-primary);
  line-height: 1.4;

  strong {
    font-weight: 600;
  }
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.85em;

  input,
  select {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    padding: 4px 6px;
    font-size: 0.9em;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: var(--accent);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 20%, transparent);
    }
  }
}

.conditional-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  .form-label {
    margin: 0;
  }
}

.form-actions {
  display: flex;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid var(--border);
  margin-top: 4px;
}

.btn-save,
.btn-cancel {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--bg-surface);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 0.85em;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
}

.btn-cancel {
  &:hover {
    border-color: var(--danger, #e55);
    color: var(--danger, #e55);
  }
}
</style>
